import React, { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, formatApiError } from "@/lib/api";
import { resolveAssetUrl } from "@/lib/assets";
import { toast } from "sonner";
import { Users, FileStack, DollarSign, CalendarRange, Upload, Trash2, Zap, AlertTriangle, RefreshCw, Ticket, Plus, TrendingUp, Percent, Wallet } from "lucide-react";
import IntegrationsTab from "@/pages/admin/IntegrationsTab";

const StatCard = ({ icon: Icon, label, value, testId, sub }) => (
  <Card className="p-6 border border-slate-200 shadow-none" data-testid={testId}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <Icon className="w-4 h-4 text-slate-400" />
    </div>
    <div className="font-heading text-3xl font-bold text-slate-900">{value}</div>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </Card>
);

const fmtUsd = (n) => `$${Number(n || 0).toFixed(4)}`;
const fmtInt = (n) => Number(n || 0).toLocaleString();

const LlmBudgetPanel = () => {
  const { lang } = useLanguage();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/llm-usage");
      setData(data);
    } catch {
      setData({});
    }
  };

  useEffect(() => {
    load();
    // Live counter: refresh every 30s so the admin sees the burn rate move.
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const saveBudget = async () => {
    const usd = parseFloat(draft);
    if (Number.isNaN(usd) || usd < 0) {
      toast.error(lang === "ar" ? "أدخل قيمة صحيحة" : "Enter a valid value");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/settings/platform", { llm_budget_usd: usd });
      toast.success(
        lang === "ar" ? "تم تحديث رصيد الذكاء الاصطناعي" : "LLM budget updated"
      );
      setEditing(false);
      setDraft("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;

  const b = data.budget || {};
  const alert24h = !!b.alert_24h;
  const noSnapshot = !b.snapshot_usd;
  const today = data.today || {};
  const week = data.last_7_days || {};

  return (
    <Card
      className={`p-6 border shadow-none ${alert24h ? "border-red-300 bg-red-50/40" : "border-slate-200"}`}
      data-testid="llm-budget-panel"
    >
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Zap className={`w-5 h-5 ${alert24h ? "text-red-600" : "text-amber-500"}`} />
          <h3 className="font-heading text-lg font-bold text-slate-900">
            {lang === "ar" ? "رصيد الذكاء الاصطناعي والاستهلاك" : "AI Budget & Usage"}
          </h3>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={load}
          data-testid="llm-budget-refresh-btn"
          className="gap-1.5"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          {lang === "ar" ? "تحديث" : "Refresh"}
        </Button>
      </div>

      {alert24h && (
        <Alert variant="destructive" className="mb-4 border-red-300 bg-red-50" data-testid="llm-budget-alert">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription className="text-red-900 text-sm leading-relaxed">
            {lang === "ar"
              ? `⚠️ تنبيه استباقي: الرصيد المتبقي ${fmtUsd(b.remaining_usd)} وبمعدّل الاستهلاك الحالي (${fmtUsd(data.avg_daily_cost_7d_usd)} يومياً) يكفي لأقل من 24 ساعة. يُنصح بتعبئة الرصيد الآن لتفادي توقّف الأبحاث.`
              : `Proactive warning: only ${fmtUsd(b.remaining_usd)} left. At current burn (${fmtUsd(data.avg_daily_cost_7d_usd)}/day) the budget will run out in under 24 hours. Top up now to prevent stuck orders.`}
          </AlertDescription>
        </Alert>
      )}

      {noSnapshot && (
        <Alert className="mb-4 bg-amber-50 border-amber-200" data-testid="llm-budget-no-snapshot">
          <AlertDescription className="text-amber-900 text-sm">
            {lang === "ar"
              ? "لم يتم تسجيل رصيد المفتاح بعد. سجّل الرصيد الحالي من لوحة Emergent → Universal Key لتفعيل التنبيهات."
              : "No budget snapshot recorded. Enter your current Universal-Key balance from the Emergent dashboard to enable alerts."}
          </AlertDescription>
        </Alert>
      )}

      {/* Budget tile */}
      <div className="grid sm:grid-cols-3 gap-4 mb-5">
        <div
          className={`p-4 rounded-md border ${alert24h ? "border-red-200 bg-red-50" : "border-slate-200 bg-slate-50"}`}
          data-testid="llm-budget-remaining-tile"
        >
          <p className="text-xs text-slate-500 mb-1">
            {lang === "ar" ? "الرصيد المتبقي" : "Remaining budget"}
          </p>
          <p className={`font-bold text-2xl ${alert24h ? "text-red-700" : "text-slate-900"}`}>
            {b.remaining_usd != null ? fmtUsd(b.remaining_usd) : "—"}
          </p>
          {b.snapshot_usd != null && (
            <p className="text-[11px] text-slate-500 mt-1">
              {lang === "ar" ? "من أصل " : "of "} {fmtUsd(b.snapshot_usd)}
            </p>
          )}
        </div>
        <div className="p-4 rounded-md border border-slate-200 bg-slate-50" data-testid="llm-burn-tile">
          <p className="text-xs text-slate-500 mb-1">
            {lang === "ar" ? "متوسط الاستهلاك اليومي (7 أيام)" : "Avg daily burn (7d)"}
          </p>
          <p className="font-bold text-2xl text-slate-900">
            {fmtUsd(data.avg_daily_cost_7d_usd)}
          </p>
          <p className="text-[11px] text-slate-500 mt-1">
            {lang === "ar" ? "اليوم: " : "Today: "} {fmtUsd(today.cost_usd)}
          </p>
        </div>
        <div className="p-4 rounded-md border border-slate-200 bg-slate-50" data-testid="llm-eta-tile">
          <p className="text-xs text-slate-500 mb-1">
            {lang === "ar" ? "المدة المتوقّعة لنفاد الرصيد" : "Estimated days remaining"}
          </p>
          <p
            className={`font-bold text-2xl ${alert24h ? "text-red-700" : "text-slate-900"}`}
            data-testid="llm-days-remaining"
          >
            {b.days_remaining_estimate != null
              ? `${b.days_remaining_estimate} ${lang === "ar" ? "يوم" : "days"}`
              : "—"}
          </p>
          {b.snapshot_at && (
            <p className="text-[11px] text-slate-500 mt-1" title={b.snapshot_at}>
              {lang === "ar" ? "آخر تعبئة: " : "Since: "}
              {new Date(b.snapshot_at).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US")}
            </p>
          )}
        </div>
      </div>

      {/* Token usage breakdown */}
      <div className="grid sm:grid-cols-2 gap-4 text-sm mb-5">
        <div className="p-4 rounded-md border border-slate-200" data-testid="llm-tokens-today">
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">
            {lang === "ar" ? "توكنز اليوم" : "Tokens today"}
          </p>
          <p className="text-slate-700">Haiku: <b>{fmtInt(today.haiku_in)}</b> → <b>{fmtInt(today.haiku_out)}</b></p>
          <p className="text-slate-700">Sonnet: <b>{fmtInt(today.sonnet_in)}</b> → <b>{fmtInt(today.sonnet_out)}</b></p>
          <p className="text-slate-500 text-xs mt-2">
            {today.orders ?? 0} {lang === "ar" ? "بحث · " : "papers · "}
            <b className="text-slate-900">{fmtUsd(today.cost_usd)}</b>
          </p>
        </div>
        <div className="p-4 rounded-md border border-slate-200" data-testid="llm-tokens-week">
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">
            {lang === "ar" ? "توكنز آخر 7 أيام" : "Tokens last 7 days"}
          </p>
          <p className="text-slate-700">Haiku: <b>{fmtInt(week.haiku_in)}</b> → <b>{fmtInt(week.haiku_out)}</b></p>
          <p className="text-slate-700">Sonnet: <b>{fmtInt(week.sonnet_in)}</b> → <b>{fmtInt(week.sonnet_out)}</b></p>
          <p className="text-slate-500 text-xs mt-2">
            {week.orders ?? 0} {lang === "ar" ? "بحث · " : "papers · "}
            <b className="text-slate-900">{fmtUsd(week.cost_usd)}</b>
          </p>
        </div>
      </div>

      {/* Manual budget snapshot updater */}
      <div className="border-t border-slate-200 pt-4">
        {!editing ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setEditing(true); setDraft(b.snapshot_usd ? String(b.snapshot_usd) : ""); }}
            data-testid="llm-budget-edit-btn"
          >
            {lang === "ar" ? "تحديث الرصيد بعد التعبئة" : "Update snapshot after top-up"}
          </Button>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-start block mb-1.5">
                {lang === "ar"
                  ? "الرصيد الحالي (USD) من Profile → Universal Key"
                  : "Current Universal-Key balance (USD)"}
              </Label>
              <Input
                type="number"
                step="0.01"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="e.g. 25.00"
                data-testid="llm-budget-input"
              />
            </div>
            <Button
              size="sm"
              onClick={saveBudget}
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="llm-budget-save-btn"
            >
              {saving ? (lang === "ar" ? "..." : "...") : (lang === "ar" ? "حفظ" : "Save")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setEditing(false); setDraft(""); }}
              data-testid="llm-budget-cancel-btn"
            >
              {lang === "ar" ? "إلغاء" : "Cancel"}
            </Button>
          </div>
        )}
        <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
          {lang === "ar"
            ? "افتح Emergent → Profile → Universal Key، انسخ الرصيد الحالي وألصقه هنا. سيُعاد ضبط حساب الاستهلاك تلقائياً."
            : "Open Emergent → Profile → Universal Key, copy the current balance and paste it here. The spent-since counter auto-resets."}
        </p>
      </div>
    </Card>
  );
};

const StatsTab = () => {
  const { t, lang } = useLanguage();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
  }, []);

  if (!stats) return <p className="text-sm text-slate-500">{t("loading")}</p>;

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard
          icon={Users}
          label={t("stat_users")}
          value={stats.users?.total ?? 0}
          testId="stat-users-card"
        />
        <StatCard
          icon={FileStack}
          label={t("stat_papers")}
          value={stats.orders?.total ?? 0}
          sub={`${stats.orders?.completed ?? 0} ${lang === "ar" ? "مكتمل" : "completed"}`}
          testId="stat-papers-card"
        />
        <StatCard
          icon={DollarSign}
          label={t("stat_revenue_day")}
          value={`$${stats.revenue?.day_usd ?? 0}`}
          sub={`${stats.revenue?.day_sar ?? 0} SAR`}
          testId="stat-revenue-day-card"
        />
        <StatCard
          icon={CalendarRange}
          label={t("stat_revenue_month")}
          value={`$${stats.revenue?.month_usd ?? 0}`}
          sub={`${stats.revenue?.month_sar ?? 0} SAR`}
          testId="stat-revenue-month-card"
        />
      </div>

      <Card className="p-6 border border-slate-200 shadow-none">
        <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">
          {lang === "ar" ? "حالة الطابور" : "Queue status"}
        </h3>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div className="flex justify-between bg-slate-50 p-4 rounded-md border border-slate-200">
            <span className="text-slate-500">{lang === "ar" ? "في الانتظار" : "Pending"}</span>
            <span className="font-bold" data-testid="queue-pending">{stats.queue?.pending ?? 0}</span>
          </div>
          <div className="flex justify-between bg-slate-50 p-4 rounded-md border border-slate-200">
            <span className="text-slate-500">{lang === "ar" ? "قيد المعالجة" : "Processing"}</span>
            <span className="font-bold" data-testid="queue-processing">{stats.queue?.processing ?? 0}</span>
          </div>
        </div>
      </Card>

      <LlmBudgetPanel />
    </div>
  );
};

const UsersTab = () => {
  const { t, lang } = useLanguage();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users?q=${encodeURIComponent(q)}`);
      setUsers(data.users);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const act = async (u, action) => {
    try {
      await api.put(`/admin/users/${u.id}`, { action });
      toast.success(t("saved"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeUser = async (u) => {
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(lang === "ar" ? "تم حذف المستخدم" : "User deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Card className="border border-slate-200 shadow-none overflow-hidden">
      <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row gap-2">
        <Input
          placeholder={lang === "ar" ? "بحث بالاسم أو البريد" : "Search by name or email"}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="users-search-input"
          className="sm:max-w-xs h-11 sm:h-10"
        />
        <Button variant="outline" onClick={load} data-testid="users-search-btn" className="h-11 sm:h-10">
          {lang === "ar" ? "بحث" : "Search"}
        </Button>
      </div>
      <div className="overflow-x-auto">
      <Table data-testid="users-table">
        <TableHeader>
          <TableRow>
            <TableHead className="text-start">{t("name_label")}</TableHead>
            <TableHead className="text-start">{t("email_label")}</TableHead>
            <TableHead className="text-start">{lang === "ar" ? "الدور" : "Role"}</TableHead>
            <TableHead className="text-start">{lang === "ar" ? "الحالة" : "Status"}</TableHead>
            <TableHead className="text-start">{t("actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                {t("loading")}
              </TableCell>
            </TableRow>
          )}
          {!loading && users.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                {lang === "ar" ? "لا يوجد مستخدمون" : "No users"}
              </TableCell>
            </TableRow>
          )}
          {users.map((u) => (
            <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
              <TableCell>{u.name}</TableCell>
              <TableCell className="text-xs text-slate-600">{u.email}</TableCell>
              <TableCell>
                <span className="text-xs px-2 py-0.5 bg-slate-100 border border-slate-200 rounded">
                  {u.role}
                </span>
              </TableCell>
              <TableCell>
                <span
                  className={`text-xs px-2 py-0.5 rounded border ${u.banned ? "bg-red-50 text-red-700 border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}
                >
                  {u.banned ? t("user_status_banned") : t("user_status_active")}
                </span>
              </TableCell>
              <TableCell>
                {u.role !== "admin" && (
                  <div className="flex gap-2 items-center flex-wrap">
                    {u.banned ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => act(u, "activate")}
                        data-testid={`user-activate-${u.id}`}
                      >
                        {t("activate")}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => act(u, "ban")}
                        data-testid={`user-ban-${u.id}`}
                      >
                        {t("ban")}
                      </Button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1.5 border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                          data-testid={`user-delete-${u.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          {lang === "ar" ? "حذف" : "Delete"}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid={`user-delete-dialog-${u.id}`}>
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-start">
                            {lang === "ar" ? "تأكيد حذف المستخدم" : "Confirm user deletion"}
                          </AlertDialogTitle>
                          <AlertDialogDescription className="text-start">
                            {lang === "ar"
                              ? `سيتم حذف الحساب (${u.email}) وجميع طلباته نهائياً. لا يمكن التراجع.`
                              : `Account (${u.email}) and all their orders will be permanently deleted. This cannot be undone.`}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid={`user-delete-cancel-${u.id}`}>
                            {t("cancel_btn")}
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => removeUser(u)}
                            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                            data-testid={`user-delete-confirm-${u.id}`}
                          >
                            {lang === "ar" ? "حذف نهائي" : "Delete permanently"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
    </Card>
  );
};

const BillingTab = () => {
  const { t, lang } = useLanguage();
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/billing").then((r) => setB(r.data)).catch(() => setB({}));
  }, []);

  if (!b) return <p className="text-sm text-slate-500">{t("loading")}</p>;

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        price_per_paper_usd: Number(b.price_per_paper_usd),
        pricing_model: b.pricing_model,
        per_piece_enabled: b.per_piece_enabled,
        package_enabled: b.package_enabled,
        subscription_enabled: b.subscription_enabled,
        tiered_pricing: b.tiered_pricing,
        promotional_free_pages: Number(b.promotional_free_pages),
        discount_percent: Number(b.discount_percent),
        model_prices_usd: b.model_prices_usd,
        moyasar_fee_percent: Number(b.moyasar_fee_percent),
        hosting_share_per_paper_usd: Number(b.hosting_share_per_paper_usd),
      };
      const { data } = await api.put("/admin/settings/billing", payload);
      setB(data);
      toast.success(t("saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const updateTier = (i, k, v) => {
    setB((s) => {
      const t = [...(s.tiered_pricing || [])];
      t[i] = { ...t[i], [k]: k === "min_pages" || k === "max_pages" ? Number(v) : Number(v) };
      return { ...s, tiered_pricing: t };
    });
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 border border-slate-200 shadow-none">
        <h3 className="font-heading text-lg font-bold text-slate-900 mb-5">
          {lang === "ar" ? "نموذج الأسعار" : "Pricing model"}
        </h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <label className="flex items-center justify-between bg-slate-50 border border-slate-200 p-3 rounded-md">
            <span className="text-sm">{lang === "ar" ? "بالقطعة" : "Per piece"}</span>
            <Switch
              checked={b.per_piece_enabled}
              onCheckedChange={(v) => setB({ ...b, per_piece_enabled: v })}
              data-testid="bill-per-piece-switch"
            />
          </label>
          <label className="flex items-center justify-between bg-slate-50 border border-slate-200 p-3 rounded-md">
            <span className="text-sm">{lang === "ar" ? "باقات" : "Packages"}</span>
            <Switch
              checked={b.package_enabled}
              onCheckedChange={(v) => setB({ ...b, package_enabled: v })}
              data-testid="bill-packages-switch"
            />
          </label>
          <label className="flex items-center justify-between bg-slate-50 border border-slate-200 p-3 rounded-md">
            <span className="text-sm">{lang === "ar" ? "اشتراك" : "Subscription"}</span>
            <Switch
              checked={b.subscription_enabled}
              onCheckedChange={(v) => setB({ ...b, subscription_enabled: v })}
              data-testid="bill-subscription-switch"
            />
          </label>
        </div>
      </Card>

      <Card className="p-6 border border-slate-200 shadow-none">
        <h3 className="font-heading text-lg font-bold text-slate-900 mb-5">
          {lang === "ar" ? "السعر الافتراضي" : "Default price"}
        </h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-start block">{lang === "ar" ? "السعر للورقة (USD)" : "Per paper (USD)"}</Label>
            <Input
              type="number"
              value={b.price_per_paper_usd}
              onChange={(e) => setB({ ...b, price_per_paper_usd: e.target.value })}
              data-testid="bill-price-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-start block">{lang === "ar" ? "خصم %" : "Discount %"}</Label>
            <Input
              type="number"
              value={b.discount_percent}
              onChange={(e) => setB({ ...b, discount_percent: e.target.value })}
              data-testid="bill-discount-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-start block">{lang === "ar" ? "صفحات مجانية" : "Promo free pages"}</Label>
            <Input
              type="number"
              value={b.promotional_free_pages}
              onChange={(e) => setB({ ...b, promotional_free_pages: e.target.value })}
              data-testid="bill-freepages-input"
            />
          </div>
        </div>
      </Card>

      <Card className="p-6 md:p-8 border border-slate-200 shadow-sm rounded-xl">
        <div className="flex items-start justify-between mb-2 gap-4 flex-wrap">
          <div>
            <h3 className="font-heading text-xl font-bold text-slate-900">
              {lang === "ar" ? "تسعير متدرّج (حسب عدد الصفحات)" : "Tiered pricing (by pages)"}
            </h3>
            <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
              {lang === "ar"
                ? "حدّد سعراً ثابتاً لكلّ نطاق من عدد الصفحات. مثال: من 1 إلى 10 صفحات → 10$. التسعير المتدرّج يُلغي السعر الافتراضي عند تطابق العدد."
                : "Set a fixed price for each page range. Example: 1-10 pages → $10. Tiers override the default per-page price when matched."}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              setB((s) => ({
                ...s,
                tiered_pricing: [...(s.tiered_pricing || []), { min_pages: 1, max_pages: 5, price_usd: 5 }],
              }))
            }
            data-testid="add-tier-btn"
            className="shrink-0"
          >
            {lang === "ar" ? "+ إضافة تسعيرة" : "+ Add tier"}
          </Button>
        </div>

        <div className="overflow-x-auto mt-5">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-slate-200">
                <th className="text-start py-3 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  {lang === "ar" ? "من صفحة" : "From page"}
                </th>
                <th className="text-start py-3 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  {lang === "ar" ? "إلى صفحة" : "To page"}
                </th>
                <th className="text-start py-3 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  {lang === "ar" ? "السعر (دولار)" : "Price (USD)"}
                </th>
                <th className="text-start py-3 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide w-20">
                  {lang === "ar" ? "حذف" : "Remove"}
                </th>
              </tr>
            </thead>
            <tbody>
              {(b.tiered_pricing || []).length === 0 && (
                <tr>
                  <td colSpan="4" className="py-6 text-center text-sm text-slate-500">
                    {lang === "ar"
                      ? "لا توجد شرائح تسعير بعد. اضغط «إضافة تسعيرة» لإنشاء الأولى."
                      : "No tiers yet. Click \"Add tier\" to create one."}
                  </td>
                </tr>
              )}
              {(b.tiered_pricing || []).map((tier, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`bill-tier-${i}`}>
                  <td className="py-3 px-3">
                    <Input
                      type="number"
                      min="1"
                      value={tier.min_pages}
                      onChange={(e) => updateTier(i, "min_pages", e.target.value)}
                      className="max-w-[100px]"
                    />
                  </td>
                  <td className="py-3 px-3">
                    <Input
                      type="number"
                      min="1"
                      value={tier.max_pages}
                      onChange={(e) => updateTier(i, "max_pages", e.target.value)}
                      className="max-w-[100px]"
                    />
                  </td>
                  <td className="py-3 px-3">
                    <div className="relative max-w-[140px]">
                      <span className="absolute start-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={tier.price_usd}
                        onChange={(e) => updateTier(i, "price_usd", e.target.value)}
                        className="ps-7"
                      />
                    </div>
                  </td>
                  <td className="py-3 px-3">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setB((s) => ({
                          ...s,
                          tiered_pricing: (s.tiered_pricing || []).filter((_, idx) => idx !== i),
                        }))
                      }
                      data-testid={`remove-tier-${i}`}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      ✕
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900 leading-relaxed">
          💡 {lang === "ar"
            ? "نصيحة: تأكّد ألاّ تتداخل النطاقات (مثلاً «1-10» ثمّ «11-20»). إذا تداخلت، النظام يستخدم أوّل شريحة مطابقة."
            : "Tip: ensure ranges do not overlap (e.g. \"1-10\" then \"11-20\"). If they do, the first matching tier is used."}
        </div>
      </Card>

      <Card className="p-6 border border-slate-200 shadow-none">
        <h3 className="font-heading text-lg font-bold text-slate-900 mb-5">
          {lang === "ar" ? "تتبع الربحية (USD)" : "Profitability tracking (USD)"}
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-start block">Haiku input /1M</Label>
            <Input
              type="number"
              step="0.01"
              value={b.model_prices_usd?.haiku_input_per_1m || 0}
              onChange={(e) => setB({ ...b, model_prices_usd: { ...b.model_prices_usd, haiku_input_per_1m: Number(e.target.value) } })}
              data-testid="bill-haiku-in"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-start block">Haiku output /1M</Label>
            <Input
              type="number"
              step="0.01"
              value={b.model_prices_usd?.haiku_output_per_1m || 0}
              onChange={(e) => setB({ ...b, model_prices_usd: { ...b.model_prices_usd, haiku_output_per_1m: Number(e.target.value) } })}
              data-testid="bill-haiku-out"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-start block">Sonnet input /1M</Label>
            <Input
              type="number"
              step="0.01"
              value={b.model_prices_usd?.sonnet_input_per_1m || 0}
              onChange={(e) => setB({ ...b, model_prices_usd: { ...b.model_prices_usd, sonnet_input_per_1m: Number(e.target.value) } })}
              data-testid="bill-sonnet-in"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-start block">Sonnet output /1M</Label>
            <Input
              type="number"
              step="0.01"
              value={b.model_prices_usd?.sonnet_output_per_1m || 0}
              onChange={(e) => setB({ ...b, model_prices_usd: { ...b.model_prices_usd, sonnet_output_per_1m: Number(e.target.value) } })}
              data-testid="bill-sonnet-out"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-start block">Moyasar fee %</Label>
            <Input
              type="number"
              step="0.1"
              value={b.moyasar_fee_percent}
              onChange={(e) => setB({ ...b, moyasar_fee_percent: e.target.value })}
              data-testid="bill-moyasar-fee"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-start block">{lang === "ar" ? "حصة الاستضافة لكل ورقة (USD)" : "Hosting share/paper (USD)"}</Label>
            <Input
              type="number"
              step="0.01"
              value={b.hosting_share_per_paper_usd}
              onChange={(e) => setB({ ...b, hosting_share_per_paper_usd: e.target.value })}
              data-testid="bill-hosting-share"
            />
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="bill-save-btn">
          {busy ? t("loading") : t("save_btn")}
        </Button>
      </div>
    </div>
  );
};

const BrandingTab = () => {
  const { t, lang } = useLanguage();
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const { data } = await api.get("/admin/settings/platform");
    setLogo(data.logo_url || null);
  };

  useEffect(() => {
    refresh();
  }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/branding/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogo(data.logo_url);
      toast.success(t("saved"));
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/admin/branding/logo");
      setLogo(null);
      toast.success(t("saved"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-8 border border-slate-200 shadow-none">
      <h3 className="font-heading text-lg font-bold text-slate-900 mb-5">
        {t("admin_tab_branding")}
      </h3>
      <div className="flex flex-wrap items-center gap-6">
        <div className="w-24 h-24 grid place-items-center bg-slate-50 border border-slate-200 rounded-md overflow-hidden">
          {logo ? (
            <img src={resolveAssetUrl(logo)} alt="logo" className="w-full h-full object-contain" data-testid="branding-logo-preview" />
          ) : (
            <span className="text-slate-400 text-xs">{lang === "ar" ? "لا يوجد" : "None"}</span>
          )}
        </div>
        <div className="flex gap-3">
          <label>
            <input
              type="file"
              accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
              className="hidden"
              onChange={upload}
              data-testid="branding-upload-input"
            />
            <Button
              type="button"
              disabled={busy}
              onClick={(e) => e.currentTarget.previousSibling.click()}
              className="bg-slate-900 hover:bg-slate-800 gap-1.5"
              data-testid="branding-upload-btn"
            >
              <Upload className="w-4 h-4" />
              {t("upload_logo")}
            </Button>
          </label>
          {logo && (
            <Button variant="outline" disabled={busy} onClick={remove} data-testid="branding-remove-btn" className="gap-1.5">
              <Trash2 className="w-4 h-4" />
              {t("remove_logo")}
            </Button>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-4">
        {lang === "ar" ? "PNG/JPG/SVG، حد أقصى 2MB" : "PNG/JPG/SVG, max 2MB"}
      </p>
    </Card>
  );
};

const SettingsTab = () => {
  const { t, lang } = useLanguage();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/platform").then((r) => setS(r.data)).catch(() => setS({}));
  }, []);

  if (!s) return <p className="text-sm text-slate-500">{t("loading")}</p>;

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        max_upload_size_mb: Number(s.max_upload_size_mb),
        rate_limit_per_hour: Number(s.rate_limit_per_hour),
        rate_limit_per_day: Number(s.rate_limit_per_day),
        max_concurrent_global: Number(s.max_concurrent_global),
        demo_enabled: !!s.demo_enabled,
        demo_free_pages: Number(s.demo_free_pages || 0),
        usd_to_sar: Number(s.usd_to_sar),
        profit_margin_alert_threshold: Number(s.profit_margin_alert_threshold),
        cost_safety_factor: Number(s.cost_safety_factor),
      };
      const { data } = await api.put("/admin/settings/platform", payload);
      setS(data);
      toast.success(t("saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, props = {}) => (
    <div className="space-y-2">
      <Label className="text-start block">{label}</Label>
      <Input
        value={s[key] ?? ""}
        onChange={(e) => setS({ ...s, [key]: e.target.value })}
        data-testid={`setting-${key}`}
        {...props}
      />
    </div>
  );

  return (
    <Card className="p-6 border border-slate-200 shadow-none">
      <h3 className="font-heading text-lg font-bold text-slate-900 mb-5">
        {t("admin_tab_settings")}
      </h3>

      <Alert className="mb-6 bg-slate-50 border-slate-200">
        <AlertDescription className="text-xs text-slate-600">
          {lang === "ar"
            ? "هذه الإعدادات تتحكم بحدود الاستخدام، الفوترة، والوضع التجريبي."
            : "These settings control rate limits, billing constants and demo mode."}
        </AlertDescription>
      </Alert>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {field("rate_limit_per_hour", lang === "ar" ? "الحد/الساعة" : "Per-hour limit", { type: "number" })}
        {field("rate_limit_per_day", lang === "ar" ? "الحد/اليوم" : "Per-day limit", { type: "number" })}
        {field("max_concurrent_global", lang === "ar" ? "أقصى عدد متزامن" : "Global concurrency", { type: "number" })}
        {field("max_upload_size_mb", lang === "ar" ? "حجم الرفع الأقصى (MB)" : "Max upload size (MB)", { type: "number" })}
        {field("usd_to_sar", lang === "ar" ? "سعر صرف USD→SAR" : "USD→SAR rate", { type: "number", step: "0.01" })}
        {field("cost_safety_factor", lang === "ar" ? "معامل أمان التكلفة" : "Cost safety factor", { type: "number", step: "0.1" })}
        {field("profit_margin_alert_threshold", lang === "ar" ? "حد تنبيه الهامش" : "Margin alert threshold (0-1)", { type: "number", step: "0.05" })}
        {field("demo_free_pages", lang === "ar" ? "صفحات تجريبية مجانية" : "Demo free pages", { type: "number" })}
      </div>

      <label className="flex items-center justify-between bg-slate-50 border border-slate-200 p-3 rounded-md mt-5">
        <span className="text-sm">{lang === "ar" ? "تفعيل الوضع التجريبي" : "Enable demo mode"}</span>
        <Switch
          checked={!!s.demo_enabled}
          onCheckedChange={(v) => setS({ ...s, demo_enabled: v })}
          data-testid="setting-demo-enabled"
        />
      </label>

      <div className="flex justify-end mt-6">
        <Button onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="settings-save-btn">
          {busy ? t("loading") : t("save_btn")}
        </Button>
      </div>
    </Card>
  );
};

const CouponsTab = () => {
  const { lang } = useLanguage();
  const T = (ar, en) => (lang === "ar" ? ar : en);
  const blank = { code: "", discount_type: "percent", discount_value: "", start_at: "", end_at: "", usage_limit: "", active: true };
  const [coupons, setCoupons] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(blank);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/coupons/analytics");
      setCoupons(data.coupons || []);
      setSummary(data.summary || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code.trim() || (form.discount_type !== "free" && !form.discount_value)) {
      toast.error(T("أدخل الكود وقيمة الخصم", "Enter code and discount value"));
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/coupons", {
        code: form.code.trim().toUpperCase(),
        discount_type: form.discount_type,
        discount_value: form.discount_type === "free" ? 0 : Number(form.discount_value),
        start_at: form.start_at ? new Date(form.start_at).toISOString() : null,
        end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
        usage_limit: form.usage_limit ? Number(form.usage_limit) : null,
        active: form.active,
      });
      toast.success(T("تم إنشاء الكوبون", "Coupon created"));
      setForm(blank);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try { await api.put(`/admin/coupons/${c.id}`, { active: !c.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (c) => {
    try { await api.delete(`/admin/coupons/${c.id}`); toast.success(T("تم الحذف", "Deleted")); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const fmtDiscount = (c) => (c.discount_type === "free" ? T("مجاني", "Free") : c.discount_type === "percent" ? `${c.discount_value}%` : `${c.discount_value} SAR`);
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US") : "—");
  const fmtSar = (n) => `${Number(n || 0).toLocaleString(lang === "ar" ? "ar-SA" : "en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${T("ر.س", "SAR")}`;

  return (
    <div className="space-y-6" data-testid="admin-coupons-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="coupon-analytics-summary">
        <StatCard
          icon={Ticket}
          label={T("الكوبونات", "Coupons")}
          value={summary ? `${summary.active_coupons}/${summary.total_coupons}` : "—"}
          sub={T("مفعّلة / الإجمالي", "active / total")}
          testId="coupon-stat-count"
        />
        <StatCard
          icon={TrendingUp}
          label={T("مرات الاستخدام", "Redemptions")}
          value={summary ? fmtInt(summary.total_redemptions) : "—"}
          sub={T("طلبات مدفوعة باستخدام كوبون", "paid orders with a coupon")}
          testId="coupon-stat-redemptions"
        />
        <StatCard
          icon={Percent}
          label={T("إجمالي الخصومات", "Total discount")}
          value={summary ? fmtSar(summary.total_discount_sar) : "—"}
          sub={T("مجموع ما تم خصمه", "total amount discounted")}
          testId="coupon-stat-discount"
        />
        <StatCard
          icon={Wallet}
          label={T("الإيرادات المُحصّلة", "Revenue collected")}
          value={summary ? fmtSar(summary.total_revenue_sar) : "—"}
          sub={T("من طلبات استخدمت كوبوناً", "from coupon orders")}
          testId="coupon-stat-revenue"
        />
      </div>

      <Card className="p-5 sm:p-6 border border-slate-200 shadow-none">
        <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Ticket className="w-5 h-5 text-slate-500" />
          {T("إنشاء كوبون جديد", "Create coupon")}
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs">{T("الكود", "Code")}</Label>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="SALE20" data-testid="coupon-form-code" className="uppercase" />
          </div>
          <div>
            <Label className="text-xs">{T("نوع الخصم", "Discount type")}</Label>
            <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value, discount_value: e.target.value === "free" ? "" : form.discount_value })} data-testid="coupon-form-type" className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
              <option value="percent">{T("نسبة مئوية (%)", "Percentage (%)")}</option>
              <option value="fixed">{T("مبلغ ثابت (ريال)", "Fixed (SAR)")}</option>
              <option value="free">{T("مجاني (بدون دفع)", "Free (no payment)")}</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">{form.discount_type === "percent" ? T("النسبة %", "Percent %") : form.discount_type === "fixed" ? T("المبلغ (ريال)", "Amount SAR") : T("القيمة", "Value")}</Label>
            <Input
              type="number"
              min="1"
              value={form.discount_type === "free" ? "" : form.discount_value}
              disabled={form.discount_type === "free"}
              onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
              placeholder={form.discount_type === "free" ? T("مجاني", "Free") : form.discount_type === "percent" ? "20" : "15"}
              data-testid="coupon-form-value"
            />
          </div>
          <div>
            <Label className="text-xs">{T("تاريخ البدء (اختياري)", "Start (optional)")}</Label>
            <Input type="date" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} data-testid="coupon-form-start" />
          </div>
          <div>
            <Label className="text-xs">{T("تاريخ الانتهاء (اختياري)", "End (optional)")}</Label>
            <Input type="date" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} data-testid="coupon-form-end" />
          </div>
          <div>
            <Label className="text-xs">{T("حد الاستخدام (اختياري)", "Usage limit (optional)")}</Label>
            <Input type="number" min="1" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} placeholder={T("غير محدود", "Unlimited")} data-testid="coupon-form-limit" />
          </div>
        </div>
        <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="coupon-form-active" />
            <span className="text-sm text-slate-600">{T("مفعّل", "Active")}</span>
          </div>
          <Button onClick={create} disabled={saving} data-testid="coupon-create-btn" className="bg-slate-900 hover:bg-slate-800">
            <Plus className="w-4 h-4 me-1.5" />
            {saving ? T("جارٍ...", "Saving...") : T("إنشاء الكوبون", "Create coupon")}
          </Button>
        </div>
        <p className="text-xs text-slate-400 mt-3">
          {T(
            "الكوبون قابل لإعادة الاستخدام افتراضياً بلا حدود. لتقييده، اضبط «حد الاستخدام». الحد الأدنى للدفع 1 ريال (لا يصل الخصم للصفر).",
            "Coupons are reusable by default (unlimited). Set a 'usage limit' to cap total uses. Minimum payable is 1 SAR."
          )}
        </p>
      </Card>

      <Card className="p-0 border border-slate-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="coupons-table">
            <TableHeader>
              <TableRow>
                <TableHead>{T("الكود", "Code")}</TableHead>
                <TableHead>{T("الخصم", "Discount")}</TableHead>
                <TableHead>{T("المدة", "Window")}</TableHead>
                <TableHead>{T("الاستخدام", "Usage")}</TableHead>
                <TableHead className="whitespace-nowrap">{T("إجمالي الخصم", "Total discount")}</TableHead>
                <TableHead className="whitespace-nowrap">{T("المستخدمون", "Users")}</TableHead>
                <TableHead className="whitespace-nowrap">{T("الإيرادات", "Revenue")}</TableHead>
                <TableHead>{T("الحالة", "Status")}</TableHead>
                <TableHead className="text-end">{T("إجراءات", "Actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">{T("جارٍ التحميل...", "Loading...")}</TableCell></TableRow>
              ) : coupons.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400" data-testid="coupons-empty">{T("لا توجد كوبونات بعد", "No coupons yet")}</TableCell></TableRow>
              ) : (
                coupons.map((c) => (
                  <TableRow key={c.id} data-testid={`coupon-row-${c.code}`}>
                    <TableCell className="font-bold">{c.code}</TableCell>
                    <TableCell>{fmtDiscount(c)}</TableCell>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(c.start_at)} → {fmtDate(c.end_at)}</TableCell>
                    <TableCell className="text-sm">{c.used_count || 0}{c.usage_limit ? ` / ${c.usage_limit}` : ""}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap" data-testid={`coupon-discount-${c.code}`}>{fmtSar(c.total_discount_sar)}</TableCell>
                    <TableCell className="text-sm" data-testid={`coupon-users-${c.code}`}>{fmtInt(c.unique_users)}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap" data-testid={`coupon-revenue-${c.code}`}>{fmtSar(c.revenue_sar)}</TableCell>
                    <TableCell>
                      <button onClick={() => toggleActive(c)} data-testid={`coupon-toggle-${c.code}`} className={`text-xs font-medium px-2 py-1 rounded-full transition-colors ${c.active ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                        {c.active ? T("مفعّل", "Active") : T("معطّل", "Inactive")}
                      </button>
                    </TableCell>
                    <TableCell className="text-end">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`coupon-delete-${c.code}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{T("حذف الكوبون؟", "Delete coupon?")}</AlertDialogTitle>
                            <AlertDialogDescription>{T(`سيُحذف الكود ${c.code} نهائياً.`, `Coupon ${c.code} will be permanently deleted.`)}</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{T("إلغاء", "Cancel")}</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(c)} className="bg-red-600 hover:bg-red-700" data-testid={`coupon-delete-confirm-${c.code}`}>{T("حذف", "Delete")}</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};


export const AdminDashboard = () => {
  const { t, lang } = useLanguage();
  return (
    <main className="min-h-[calc(100vh-4rem)] px-4 sm:px-6 py-6 sm:py-10" data-testid="admin-dashboard-page">
      <div className="mx-auto max-w-7xl">
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 text-start mb-6 sm:mb-8">
          {t("admin_title")}
        </h1>
        <Tabs defaultValue="stats" className="w-full">
          {/* Horizontally scrollable tab strip on mobile — keeps tap targets tall (44px) and avoids cramped wrapping. */}
          <div className="-mx-4 sm:mx-0 overflow-x-auto scrollbar-thin">
            <TabsList
              className="inline-flex w-max sm:w-auto px-4 sm:px-1 h-auto sm:h-9 gap-1"
              data-testid="admin-tabs"
            >
              <TabsTrigger value="stats" className="h-11 sm:h-7 px-4 text-sm whitespace-nowrap" data-testid="admin-tab-stats">{t("admin_tab_stats")}</TabsTrigger>
              <TabsTrigger value="users" className="h-11 sm:h-7 px-4 text-sm whitespace-nowrap" data-testid="admin-tab-users">{t("admin_tab_users")}</TabsTrigger>
              <TabsTrigger value="billing" className="h-11 sm:h-7 px-4 text-sm whitespace-nowrap" data-testid="admin-tab-billing">{t("admin_tab_billing")}</TabsTrigger>
              <TabsTrigger value="coupons" className="h-11 sm:h-7 px-4 text-sm whitespace-nowrap" data-testid="admin-tab-coupons">{lang === "ar" ? "الكوبونات" : "Coupons"}</TabsTrigger>
              <TabsTrigger value="branding" className="h-11 sm:h-7 px-4 text-sm whitespace-nowrap" data-testid="admin-tab-branding">{t("admin_tab_branding")}</TabsTrigger>
              <TabsTrigger value="integrations" className="h-11 sm:h-7 px-4 text-sm whitespace-nowrap" data-testid="admin-tab-integrations">
                {lang === "ar" ? "التكاملات" : "Integrations"}
              </TabsTrigger>
              <TabsTrigger value="settings" className="h-11 sm:h-7 px-4 text-sm whitespace-nowrap" data-testid="admin-tab-settings">{t("admin_tab_settings")}</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="stats" className="mt-6"><StatsTab /></TabsContent>
          <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
          <TabsContent value="billing" className="mt-6"><BillingTab /></TabsContent>
          <TabsContent value="coupons" className="mt-6"><CouponsTab /></TabsContent>
          <TabsContent value="branding" className="mt-6"><BrandingTab /></TabsContent>
          <TabsContent value="integrations" className="mt-6"><IntegrationsTab /></TabsContent>
          <TabsContent value="settings" className="mt-6"><SettingsTab /></TabsContent>
        </Tabs>
      </div>
    </main>
  );
};

export default AdminDashboard;
